import React, { useState, useEffect } from 'react';
import { X, Zap, MapPin, Thermometer, Clock, Rss, Video, Globe, Layout } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WidgetPicker = ({ onSelect, onCancel }) => {
  const { t } = useTranslation();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWidget, setSelectedWidget] = useState(null);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      console.log('🔍 WidgetPicker: Loading widgets from features API...');
      const token = localStorage.getItem('token');
      
      // Load active features
      const featuresResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const featuresData = await featuresResponse.json();
      const activated = featuresData.features.filter(f => f.isActivated);
      
      console.log('✅ Active features:', activated.length);
      
      // Load all instances from all features
      const allInstances = [];
      for (const feature of activated) {
        const instancesResponse = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://app.alpenscreen.ch/api'}/features/${feature.id}/instances`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const data = await instancesResponse.json();
        const instances = data.instances || [];
        
        // Add feature info to each instance
        instances.forEach(instance => {
          allInstances.push({
            ...instance,
            feature_id: feature.id,
            feature_name: feature.name
          });
        });
      }
      
      // 🔥 FILTER: Zeige nur Widgets die NICHT automatisch generiert wurden
      // Automatisch generierte Widgets haben Namen wie "weather-widget-1760747916868"
      const manualWidgets = allInstances.filter(widget => {
        // Filter aus: Namen die mit feature-id + Timestamp sind (13+ Ziffern am Ende)
        const isAutoGenerated = widget.name.match(/^[a-z-]+-\d{13,}$/);
        return !isAutoGenerated;
      });
      
      console.log('📦 Loaded widget instances:', manualWidgets.length, '(filtered from', allInstances.length, ')');
      setWidgets(manualWidgets);
    } catch (error) {
      console.error('❌ Error loading widgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedWidget) {
      console.log('✅ Selected widget:', selectedWidget);
      
      // Pass the widget data including the existing instance ID
      onSelect({
        feature_id: selectedWidget.feature_id,
        feature_instance_id: selectedWidget.id,  // Bestehende Instance ID
        duration: 30,
        config: selectedWidget.config
      });
    }
  };

  const getWidgetIcon = (featureId) => {
    switch (featureId) {
      case 'weather-widget':
        return <Thermometer className="w-8 h-8" />;
      case 'clock-widget':
        return <Clock className="w-8 h-8" />;
      case 'rss-feed-widget':
        return <Rss className="w-8 h-8" />;
      case 'streaming-widget':
        return <Video className="w-8 h-8" />;
      case 'web-content-widget':
        return <Globe className="w-8 h-8" />;
      case 'multi-zone':
        return <Layout className="w-8 h-8" />;
      default:
        return <Zap className="w-8 h-8" />;
    }
  };

  const getWidgetPreview = (widget) => {
    // Weather Widget Preview
    if (widget.feature_id === 'weather-widget') {
      const location = widget.config?.location || t('widgetPicker.unknownLocation');
      const units = widget.config?.units || 'metric';
      const updateInterval = widget.config?.updateInterval || 600;
      
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
          <div className="flex items-center gap-1">
            <MapPin size={14} />
            {location}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {units === 'metric' ? t('widgetPicker.metric') : t('widgetPicker.imperial')} • {updateInterval}s {t('widgetPicker.refresh')}
          </div>
        </div>
      );
    }
    
    // Clock Widget Preview
    if (widget.feature_id === 'clock-widget') {
      const format = widget.config?.format || '24h';
      const showDate = widget.config?.showDate ?? true;
      const showSeconds = widget.config?.showSeconds ?? true;
      const timezone = widget.config?.timezone || 'auto';
      
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
          <div className="flex items-center gap-2">
            <span>⏰ {format === '24h' ? '24-Hour' : '12-Hour'} Format</span>
          </div>
          {showDate && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              📅 Date Display Enabled
            </div>
          )}
          {showSeconds && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              🕐 Seconds Display
            </div>
          )}
          {timezone !== 'auto' && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              🌍 {timezone.replace('_', ' ')}
            </div>
          )}
        </div>
      );
    }

    // RSS Feed Widget Preview
    if (widget.feature_id === 'rss-feed-widget') {
      const feedUrl = widget.config?.feedUrl || '';
      const maxItems = widget.config?.maxItems || 10;
      
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
          <div className="text-xs truncate">
            📡 {feedUrl}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {maxItems} items
          </div>
        </div>
      );
    }

    // Streaming Widget Preview
    if (widget.feature_id === 'streaming-widget') {
      const platform = widget.config?.platform || 'youtube';
      const channelId = widget.config?.channelId || '';
      
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
          <div className="flex items-center gap-2">
            <span>📺 {platform === 'youtube' ? 'YouTube' : 'Twitch'}</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {channelId}
          </div>
        </div>
      );
    }

    // Web Content Widget Preview
    if (widget.feature_id === 'web-content-widget') {
      const url = widget.config?.url || '';
      const displayMode = widget.config?.displayMode || 'auto';
      
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
          <div className="text-xs truncate">
            🌐 {url}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {displayMode === 'iframe' ? 'iframe' : displayMode === 'screenshot' ? 'Screenshot' : 'Auto'}
          </div>
        </div>
      );
    }

    // Multi-Zone Preview
    if (widget.feature_id === 'multi-zone') {
      const layoutType = widget.config?.layoutType || '2-horizontal';
      const zoneCount = widget.config?.zones?.length || 0;
      
      return (
        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 space-y-1">
          <div className="flex items-center gap-2">
            <Layout size={14} />
            {t(`widgets.multiZone.layouts.${layoutType}`)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {zoneCount} {t('widgets.multiZone.zones')}
          </div>
        </div>
      );
    }
    
    return null;
  };

  const getWidgetTypeLabel = (featureId) => {
    switch (featureId) {
      case 'weather-widget':
        return t('widgetPicker.weatherWidget');
      case 'clock-widget':
        return t('widgetPicker.clockWidget');
      case 'rss-feed-widget':
        return t('widgetPicker.rssWidget');
      case 'streaming-widget':
        return t('widgetPicker.streamingWidget');
      case 'web-content-widget':
        return t('widgetPicker.webContentWidget');
      case 'multi-zone':
        return t('widgets.multiZone.widgetName');
      default:
        return featureId;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {t('widgetPicker.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('widgetPicker.subtitle')}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-500 dark:text-gray-400 mt-4">
                {t('widgetPicker.loading')}
              </p>
            </div>
          ) : widgets.length === 0 ? (
            <div className="text-center py-12">
              <Zap size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {t('widgetPicker.noWidgets')}
              </p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                {t('widgetPicker.createFirst')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {widgets.map((widget) => (
                <button
                  key={widget.id}
                  onClick={() => setSelectedWidget(widget)}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    selectedWidget?.id === widget.id
                      ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedWidget?.id === widget.id
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      {getWidgetIcon(widget.feature_id)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                        {widget.name}
                      </h3>
                      {getWidgetPreview(widget)}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        {getWidgetTypeLabel(widget.feature_id)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            {t('widgetPicker.cancel')}
          </button>
          <button
            onClick={handleSelect}
            disabled={!selectedWidget}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              selectedWidget
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-600 cursor-not-allowed'
            }`}
          >
            {t('widgetPicker.addToPlaylist')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WidgetPicker;